import { createFeatureSelector, createSelector } from '@ngrx/store';
import type { ISecapState, SecapResourceKey } from '../../../interfaces/secap/ISecapState';
import { buildSecapFieldsViewModel } from '../../../utils/secap/secap-fields.mapper';
import { buildSecapSourcesViewModel } from '../../../utils/secap/secap-sources.mapper';
import { buildSecapTemplateCompilationViewModel } from '../../../utils/secap/secap-template-compilation.mapper';
import { SECAP_FEATURE_KEY } from '../../reducers/secap/secap.reducer';

export const selectSecapState = createFeatureSelector<ISecapState>(SECAP_FEATURE_KEY);

export const selectSecapProjectUuid = createSelector(
  selectSecapState,
  (state) => state.projectUuid,
);

export const selectSecapProjectStatus = createSelector(
  selectSecapState,
  (state) => state.projectStatus,
);

export const selectSecapVersions = createSelector(
  selectSecapState,
  (state) => state.versions,
);

export const selectSecapSelectedVersionUuid = createSelector(
  selectSecapState,
  (state) => state.selectedVersionUuid,
);

export const selectSecapSelectedVersion = createSelector(
  selectSecapVersions,
  selectSecapSelectedVersionUuid,
  (versions, selectedVersionUuid) =>
    versions.find((version) => version.uuid === selectedVersionUuid) ?? null,
);

export const selectSecapVersionSaving = createSelector(
  selectSecapState,
  (state) => state.versionSaving,
);

export const selectSecapVersionSaveError = createSelector(
  selectSecapState,
  (state) => state.versionSaveError,
);

export const selectSecapRuntimeCatalog = createSelector(
  selectSecapState,
  (state) => state.runtimeCatalog,
);

export const selectSecapRuntimeIndicators = createSelector(
  selectSecapRuntimeCatalog,
  (catalog) => catalog?.indicators ?? [],
);

export const selectSecapTemplateCoverage = createSelector(
  selectSecapState,
  (state) => state.templateCoverage,
);

export const selectSecapFieldsViewModel = createSelector(
  selectSecapRuntimeCatalog,
  selectSecapTemplateCoverage,
  selectSecapSelectedVersion,
  buildSecapFieldsViewModel,
);

export const selectSecapDataSources = createSelector(
  selectSecapState,
  (state) => state.dataSources,
);

export const selectSecapSourcesViewModel = createSelector(
  selectSecapDataSources,
  selectSecapRuntimeCatalog,
  buildSecapSourcesViewModel,
);

export const selectSecapDataSourceDetails = createSelector(
  selectSecapState,
  (state) => state.dataSourceDetails,
);

export const selectSecapDataSourceDetailLoading = createSelector(
  selectSecapState,
  (state) => state.dataSourceDetailLoading,
);

export const selectSecapDataSourceDetailErrors = createSelector(
  selectSecapState,
  (state) => state.dataSourceDetailErrors,
);

export const selectSecapTemplateCompilations = createSelector(
  selectSecapState,
  (state) => state.templateCompilations,
);

export const selectSecapSelectedTemplateCompilationUuid = createSelector(
  selectSecapState,
  (state) => state.selectedTemplateCompilationUuid,
);

export const selectSecapSelectedTemplateCompilation = createSelector(
  selectSecapState,
  (state) => state.selectedTemplateCompilation,
);

export const selectSecapTemplateCompilationLoading = createSelector(
  selectSecapState,
  (state) => state.templateCompilationLoading,
);

export const selectSecapTemplateCompilationError = createSelector(
  selectSecapState,
  (state) => state.templateCompilationError,
);

export const selectSecapTemplateCompilationPreparing = createSelector(
  selectSecapState,
  (state) => state.templateCompilationPreparing,
);

export const selectSecapTemplateCompilationPrepareError = createSelector(
  selectSecapState,
  (state) => state.templateCompilationPrepareError,
);

export const selectSecapTemplateCompilationDecisionSaving = createSelector(
  selectSecapState,
  (state) => state.templateCompilationDecisionSaving,
);

export const selectSecapTemplateCompilationDecisionError = createSelector(
  selectSecapState,
  (state) => state.templateCompilationDecisionError,
);

export const selectSecapTemplateCompilationFinalizing = createSelector(
  selectSecapState,
  (state) => state.templateCompilationFinalizing,
);

export const selectSecapTemplateCompilationFinalizeError = createSelector(
  selectSecapState,
  (state) => state.templateCompilationFinalizeError,
);

export const selectSecapTemplateCompilationDownloadLoading = createSelector(
  selectSecapState,
  (state) => state.templateCompilationDownloadLoading,
);

export const selectSecapTemplateCompilationDownloadError = createSelector(
  selectSecapState,
  (state) => state.templateCompilationDownloadError,
);

export const selectSecapTemplateCompilationViewModel = createSelector(
  selectSecapTemplateCompilations,
  selectSecapSelectedTemplateCompilation,
  buildSecapTemplateCompilationViewModel,
);

export const selectSecapHasProvisioning = createSelector(
  selectSecapProjectStatus,
  (status) =>
    Boolean(status?.operational_provisioning) || Boolean(status?.draft_provisioning),
);

export const selectSecapResourceLoading = (resource: SecapResourceKey) =>
  createSelector(selectSecapState, (state) => state.loading[resource]);

export const selectSecapResourceLoaded = (resource: SecapResourceKey) =>
  createSelector(selectSecapState, (state) => state.loaded[resource]);

export const selectSecapResourceError = (resource: SecapResourceKey) =>
  createSelector(selectSecapState, (state) => state.errors[resource]);
